import { useState } from "react";
import { BrandLockup } from "@/shell/CandidateFlow";

export function RecruiterForgot() {
  const [email, setEmail]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);

    try {
      // 1. Fetch a fresh CSRF token from the JSON endpoint.
      const csrfRes = await fetch("/api/v1/csrf-token", { credentials: "include" });
      if (!csrfRes.ok) throw new Error("Could not obtain security token. Please refresh and try again.");
      const csrfData = (await csrfRes.json()) as { token?: string; csrf_token?: string };
      const csrfToken = csrfData.token ?? csrfData.csrf_token ?? "";

      // 2. POST as FormData — Flask-WTF reads request.form.
      const body = new URLSearchParams();
      body.set("email", email);
      body.set("csrf_token", csrfToken);

      const res = await fetch("/auth/forgot", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRFToken": csrfToken,
        },
        body: body.toString(),
      });

      if (res.ok || res.status === 200) {
        setSuccess(true);
      } else {
        throw new Error("Something went wrong. Please try again.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }

    setBusy(false);
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FBFAFE",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: "#FFFFFF",
          borderRadius: 16,
          padding: 40,
          boxShadow: "0 1px 4px rgba(45,30,90,0.08)",
        }}
      >
        <div style={{ display: "flex", marginBottom: 28 }}>
          <BrandLockup />
        </div>

        {/* Heading */}
        <h1
          style={{
            margin: "0 0 6px",
            fontSize: 26,
            fontWeight: 700,
            color: "#17131F",
            letterSpacing: "-0.4px",
            lineHeight: 1.2,
          }}
        >
          Reset your password
        </h1>
        <p
          style={{
            margin: "0 0 28px",
            fontSize: 14,
            color: "#7C7590",
          }}
        >
          Enter your email and we&apos;ll send you a reset link.
        </p>

        {/* Success callout */}
        {success && (
          <div
            role="status"
            style={{
              marginBottom: 20,
              padding: "12px 14px",
              background: "#F0FDFA",
              border: "1px solid #99F6E4",
              borderRadius: 8,
              fontSize: 13.5,
              color: "#7A6BE0",
            }}
          >
            If that email is registered, you&apos;ll receive a reset link shortly.
          </div>
        )}

        {/* Error alert */}
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: 20,
              padding: "12px 14px",
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              fontSize: 13.5,
              color: "#B91C1C",
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Email field */}
          <div>
            <label
              htmlFor="forgot-email"
              style={{
                display: "block",
                fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                color: "#7C7590",
                marginBottom: 8,
              }}
            >
              Email
            </label>
            <input
              id="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
              placeholder="you@firm.com"
              style={{
                display: "block",
                width: "100%",
                boxSizing: "border-box",
                background: "#FFFFFF",
                border: "1px solid #E0DBD3",
                borderRadius: 8,
                padding: "12px 16px",
                fontSize: 14,
                color: "#17131F",
                outline: "none",
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = "#7A6BE0"; }}
              onBlur={(e)  => { e.currentTarget.style.borderColor = "#E0DBD3"; }}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={busy || !email}
            style={{
              width: "100%",
              height: 48,
              background: busy || !email ? "#4B5563" : "#17131F",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 9999,
              fontSize: 15,
              fontWeight: 600,
              cursor: busy || !email ? "default" : "pointer",
              opacity: busy || !email ? 0.6 : 1,
              transition: "opacity 0.12s, background 0.12s",
              letterSpacing: "0.01em",
            }}
          >
            {busy ? "Sending…" : "Send reset link"}
          </button>
        </form>

        {/* Back to sign in */}
        <p
          style={{
            marginTop: 24,
            textAlign: "center",
            fontSize: 13.5,
            color: "#7C7590",
          }}
        >
          <a
            href="/auth/login"
            style={{
              color: "#7A6BE0",
              textDecoration: "none",
              fontWeight: 500,
            }}
            onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseOut={(e)  => { e.currentTarget.style.textDecoration = "none"; }}
          >
            Back to sign in
          </a>
        </p>
      </div>
    </div>
  );
}
